import { useState, type FormEvent } from 'react';
import { adminSignupGateway } from '../../services/AdminSignupGateway';
import { formatZipDisplay, zipDigitsFromInput } from '../../../../shared/formatting';
import type { StepProps } from '../AdminSignUpPage';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';

// Matches the approved FINAL HTML's own `#billingState`/`#mailingState` option list exactly.
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

/** Step 2 — Company Address. Implements the FINAL approved HTML's single "Same as Billing Address"
 * checkbox mechanism only (Admin Sign Up Decision 9) — the alternate tabbed Billing/Mailing UI shown in
 * the supplied PDF/screenshots was explicitly not built. No "Skip for now" on this step, matching the
 * approved HTML. */
export interface AddressStepProps extends StepProps {
  /** EPN-35 Partner Sign Up override — default matches Admin Sign Up's own approved copy exactly. */
  heading?: string;
}

export function AddressStep({ goNext, goBack, heading = 'Step 2: Company Address' }: AddressStepProps) {
  const [branchName, setBranchName] = useState('');
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCountry, setBillingCountry] = useState('United States');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [mailingAddressLine1, setMailingAddressLine1] = useState('');
  const [mailingAddressLine2, setMailingAddressLine2] = useState('');
  const [mailingCountry, setMailingCountry] = useState('United States');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingState, setMailingState] = useState('');
  const [mailingZip, setMailingZip] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminSignupGateway.saveAddress({
        branchName: branchName || null,
        billingAddressLine1,
        billingAddressLine2: billingAddressLine2 || null,
        billingCountry,
        billingCity,
        billingState,
        billingZip,
        mailingSameAsBilling: sameAsBilling,
        mailingAddressLine1: sameAsBilling ? null : mailingAddressLine1,
        mailingAddressLine2: sameAsBilling ? null : mailingAddressLine2 || null,
        mailingCountry: sameAsBilling ? null : mailingCountry,
        mailingCity: sameAsBilling ? null : mailingCity,
        mailingState: sameAsBilling ? null : mailingState,
        mailingZip: sameAsBilling ? null : mailingZip,
      });
      goNext();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save the company address.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="screen active" data-testid="address-step">
      <div className="screen-icon" aria-hidden="true">
        📍
      </div>
      <h2>{heading}</h2>

      <form onSubmit={handleSubmit}>
        <div className="field full">
          <label htmlFor="branchName">
            Branch <span className="req">*</span>
          </label>
          <input className="control" id="branchName" value={branchName} onChange={(e) => setBranchName(e.target.value)} required />
        </div>

        <div className="section-heading">Billing Address</div>
        <div className="field-grid">
          <div className="field">
            <label htmlFor="billingAddressLine1">
              Address Line 1 <span className="req">*</span>
            </label>
            <input className="control" id="billingAddressLine1" value={billingAddressLine1} onChange={(e) => setBillingAddressLine1(e.target.value)} required />
          </div>

          <div className="field">
            <label htmlFor="billingAddressLine2">Address Line 2</label>
            <input className="control" id="billingAddressLine2" value={billingAddressLine2} onChange={(e) => setBillingAddressLine2(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="billingCountry">
              Country <span className="req">*</span>
            </label>
            <input className="control" id="billingCountry" value={billingCountry} onChange={(e) => setBillingCountry(e.target.value)} required />
          </div>

          <div className="field">
            <label htmlFor="billingCity">
              City <span className="req">*</span>
            </label>
            <input className="control" id="billingCity" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} required />
          </div>

          <div className="field">
            <label htmlFor="billingState">
              State <span className="req">*</span>
            </label>
            <select className="control" id="billingState" value={billingState} onChange={(e) => setBillingState(e.target.value)} required>
              <option value="">Select</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="billingZip">
              Zip Code <span className="req">*</span>
            </label>
            <input
              className="control"
              id="billingZip"
              inputMode="numeric"
              value={formatZipDisplay(billingZip)}
              onChange={(e) => setBillingZip(zipDigitsFromInput(e.target.value))}
              placeholder="XXXXX or XXXXX-XXXX"
              maxLength={10}
              required
            />
          </div>
        </div>

        <div className="address-bottom">
          <label className="checkbox-row" htmlFor="sameAsBilling">
            <input id="sameAsBilling" type="checkbox" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} />
            <span>
              <strong>Mailing Address:</strong> Same as Billing Address
            </span>
          </label>
        </div>

        {!sameAsBilling && (
          <div className="mailing-section">
            <div className="section-heading">Mailing Address</div>
            <div className="field-grid">
              <div className="field">
                <label htmlFor="mailingAddressLine1">
                  Address Line 1 <span className="req">*</span>
                </label>
                <input className="control" id="mailingAddressLine1" value={mailingAddressLine1} onChange={(e) => setMailingAddressLine1(e.target.value)} required />
              </div>

              <div className="field">
                <label htmlFor="mailingAddressLine2">Address Line 2</label>
                <input className="control" id="mailingAddressLine2" value={mailingAddressLine2} onChange={(e) => setMailingAddressLine2(e.target.value)} />
              </div>

              <div className="field">
                <label htmlFor="mailingCountry">
                  Country <span className="req">*</span>
                </label>
                <input className="control" id="mailingCountry" value={mailingCountry} onChange={(e) => setMailingCountry(e.target.value)} required />
              </div>

              <div className="field">
                <label htmlFor="mailingCity">
                  City <span className="req">*</span>
                </label>
                <input className="control" id="mailingCity" value={mailingCity} onChange={(e) => setMailingCity(e.target.value)} required />
              </div>

              <div className="field">
                <label htmlFor="mailingState">
                  State <span className="req">*</span>
                </label>
                <select className="control" id="mailingState" value={mailingState} onChange={(e) => setMailingState(e.target.value)} required>
                  <option value="">Select</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="mailingZip">
                  Zip Code <span className="req">*</span>
                </label>
                <input
                  className="control"
                  id="mailingZip"
                  inputMode="numeric"
                  value={formatZipDisplay(mailingZip)}
                  onChange={(e) => setMailingZip(zipDigitsFromInput(e.target.value))}
                  placeholder="XXXXX or XXXXX-XXXX"
                  maxLength={10}
                  required
                />
              </div>
            </div>
          </div>
        )}

        {error && (
          <p className="error-line" role="alert">
            <span className="info-icon">i</span>
            <span>{error}</span>
          </p>
        )}

        <div className="actions">
          <button className="btn btn-back" type="button" onClick={goBack}>
            Go Back
          </button>
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
